/**
 * Web-Push subscription helpers.
 * Uses the standard Push API — no third-party SDK.
 */
import api from './api';

/** Base64URL → Uint8Array. Required by PushManager.subscribe(). */
function urlBase64ToUint8Array(base64) {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export const push = {
  supported() {
    return typeof window !== 'undefined'
      && 'serviceWorker' in navigator
      && 'PushManager' in window
      && 'Notification' in window;
  },

  permission() { return typeof Notification !== 'undefined' ? Notification.permission : 'denied'; },

  async registerSW() {
    if (!('serviceWorker' in navigator)) return null;
    try {
      const reg = await navigator.serviceWorker.register('/service-worker.js', { scope: '/' });
      await navigator.serviceWorker.ready;
      return reg;
    } catch (e) { return null; }
  },

  async currentSubscription() {
    const reg = await navigator.serviceWorker.getRegistration('/');
    if (!reg) return null;
    return reg.pushManager.getSubscription();
  },

  async enable() {
    if (!this.supported()) throw new Error('Push not supported on this device.');
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') throw new Error('Notifications permission denied.');
    const reg = await this.registerSW();
    if (!reg) throw new Error('Could not register service worker.');
    const { data } = await api.get('/push/vapid-public-key');
    if (!data.configured || !data.public_key) throw new Error('Push not configured on the server.');
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
    }
    await api.post('/push/subscribe', { subscription: sub.toJSON() });
    return true;
  },

  async disable() {
    const sub = await this.currentSubscription();
    if (!sub) return true;
    try { await api.post('/push/unsubscribe', { endpoint: sub.endpoint }); } catch (_) { /* ignore */ }
    try { await sub.unsubscribe(); } catch (_) { /* ignore */ }
    return true;
  },

  async sendTest() {
    const { data } = await api.post('/push/test');
    return data;
  },
};

export default push;
